import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { AgentStreamEvent, sseEvents } from './agent-stream';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import {
  AgentStatePatch,
  AgentSummariesRequest,
  AgentSummariesResponse,
  AgentTurnAction,
  InterviewDepth,
  ProgressResponse,
  ViewResponse,
} from '../models/agent-chat.models';

export interface TurnMessage {
  role: string;
  content: string;
}

export interface TurnInput {
  messages?: TurnMessage[];
  resume?: TurnConfig;
}

export interface TurnRequest {
  input: TurnInput;
  action?: AgentTurnAction;
}

export interface TurnConfig {
  approved?: boolean;
  fill_gaps?: boolean;
  rejected?: boolean;
  feedback?: string;
  selected?: string[];
  other?: string;
  text?: string;
}

export interface AckRequest {
  message: string;
}

export interface AckResponse {
  ack: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AgentsService extends ApiService {
  private readonly THREAD_ID_HEADER = 'X-Mojo-Thread-Id';
  private readonly RUN_ID_HEADER = 'X-Run-ID';

  constructor() {
    super();
    this.url = 'agents/';
  }

  private withRunId(headers: HttpHeaders, runId?: string): HttpHeaders {
    return runId ? headers.set(this.RUN_ID_HEADER, runId) : headers;
  }

  public sendMessageTurn(
    message: string,
    threadId: string,
    action?: AgentTurnAction,
    jdText?: string,
    interviewDepth?: InterviewDepth,
    runId?: string,
  ): Observable<ViewResponse> {
    const originalHeaders = this.headers;
    this.headers = this.withRunId(this.headers.set(this.THREAD_ID_HEADER, threadId), runId);

    const turnRequest: TurnRequest = {
      input: {
        messages: [{ role: 'user', content: message }],
        ...(jdText ? { jd_text: jdText } : {}),
      },
      ...(action ? { action } : {}),
      ...(interviewDepth ? { config: { interview_depth: interviewDepth } } : {}),
    };

    const request = this.post<TurnRequest, ViewResponse>({
      url: 'turn',
      body: turnRequest,
    });

    this.headers = originalHeaders;

    return request;
  }

  public resumeTurn(
    threadId: string,
    resumeConfig: TurnConfig,
    action?: AgentTurnAction,
    interviewDepth?: InterviewDepth,
    runId?: string,
  ): Observable<ViewResponse> {
    const originalHeaders = this.headers;
    this.headers = this.withRunId(this.headers.set(this.THREAD_ID_HEADER, threadId), runId);

    const turnRequest: TurnRequest = {
      input: {
        resume: resumeConfig,
      },
      ...(action ? { action } : {}),
      ...(interviewDepth ? { config: { interview_depth: interviewDepth } } : {}),
    };

    const request = this.post<TurnRequest, ViewResponse>({
      url: 'turn',
      body: turnRequest,
    });

    this.headers = originalHeaders;

    return request;
  }

  private turnStream(
    threadId: string,
    runId: string,
    body: TurnRequest,
  ): Observable<AgentStreamEvent> {
    const headers = this.withRunId(this.headers.set(this.THREAD_ID_HEADER, threadId), runId).set(
      'Accept',
      'text/event-stream',
    );

    return sseEvents(
      this.http.post(`${this.baseUrl}turn/stream`, JSON.stringify(body), {
        headers,
        observe: 'events',
        responseType: 'text',
        reportProgress: true,
      }),
    );
  }

  public sendMessageTurnStream(
    message: string,
    threadId: string,
    runId: string,
    action?: AgentTurnAction,
    jdText?: string,
    interviewDepth?: InterviewDepth,
  ): Observable<AgentStreamEvent> {
    return this.turnStream(threadId, runId, {
      input: {
        messages: [{ role: 'user', content: message }],
        ...(jdText ? { jd_text: jdText } : {}),
      },
      ...(action ? { action } : {}),
      ...(interviewDepth ? { config: { interview_depth: interviewDepth } } : {}),
    } as TurnRequest);
  }

  public resumeTurnStream(
    threadId: string,
    runId: string,
    resumeConfig: TurnConfig,
    action?: AgentTurnAction,
    interviewDepth?: InterviewDepth,
  ): Observable<AgentStreamEvent> {
    return this.turnStream(threadId, runId, {
      input: { resume: resumeConfig },
      ...(action ? { action } : {}),
      ...(interviewDepth ? { config: { interview_depth: interviewDepth } } : {}),
    } as TurnRequest);
  }

  public sendAck(prompt: string, threadId: string): Observable<AckResponse> {
    const originalHeaders = this.headers;
    this.headers = this.headers.set(this.THREAD_ID_HEADER, threadId);

    const request = this.post<AckRequest, AckResponse>({
      url: 'ack',
      body: { message: prompt },
    });

    this.headers = originalHeaders;

    return request;
  }

  public getSessionState(threadId: string): Observable<ViewResponse> {
    const originalHeaders = this.headers;
    this.headers = this.headers.set(this.THREAD_ID_HEADER, threadId);

    const request = this.get<ViewResponse>({
      url: 'state',
    });

    // Reset back to original
    this.headers = originalHeaders;

    return request;
  }

  public patchSessionState(threadId: string, body: AgentStatePatch): Observable<void> {
    const originalHeaders = this.headers;
    this.headers = this.headers.set(this.THREAD_ID_HEADER, threadId);

    const request = this.patch<AgentStatePatch, void>({
      url: 'state',
      body,
    });

    this.headers = originalHeaders;

    return request;
  }

  public applyStagedChanges(threadId: string, runId?: string): Observable<ViewResponse> {
    const originalHeaders = this.headers;
    this.headers = this.withRunId(this.headers.set(this.THREAD_ID_HEADER, threadId), runId);

    const request = this.post<TurnRequest, ViewResponse>({
      url: 'turn',
      body: {
        input: {},
        action: 'apply_staged',
      },
    });

    this.headers = originalHeaders;

    return request;
  }

  public getProgress(threadId: string, runId: string): Observable<ProgressResponse> {
    const originalHeaders = this.headers;
    this.headers = this.withRunId(this.headers.set(this.THREAD_ID_HEADER, threadId), runId);

    const request = this.get<ProgressResponse>({
      url: 'progress',
    });

    this.headers = originalHeaders;

    return request;
  }

  public getChatbotCandidates(
    page: number,
    threadId: string,
    projectId: number,
  ): Observable<unknown> {
    const originalHeaders = this.headers;
    this.headers = this.headers.set(this.THREAD_ID_HEADER, threadId);
    let parameters = `page=${page}`;
    if (projectId !== 0) {
      parameters = `page=${page}&excludeTalentPoolId=${projectId}`;
    }

    const request = this.post<{ page: number }, unknown>({
      url: `results?${parameters}`,
    });

    this.headers = originalHeaders;

    return request;
  }

  public generateAgentSummaries(
    threadId: string,
    candidateIds: string[],
  ): Observable<AgentSummariesResponse> {
    const originalHeaders = this.headers;
    this.headers = this.headers.set(this.THREAD_ID_HEADER, threadId);

    const request = this.post<AgentSummariesRequest, AgentSummariesResponse>({
      url: 'summaries',
      body: { candidate_ids: candidateIds },
    });

    this.headers = originalHeaders;

    return request;
  }
}
